import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["trigger", "toggle", "successMessage"]
  static values = { 
    triggerValue: String, // Value that triggers showing the form
    hideClass: { type: String, default: 'd-none' },
    successClass: { type: String, default: 'border-success' },
    showSuccessMessage: { type: Boolean, default: false },
    successDuration: { type: Number, default: 3000 }
  }

  connect() {
    this.bindTriggerEvents()
    this.initializeFormState()
  }

  bindTriggerEvents() {
    this.triggerTargets.forEach(trigger => {
      trigger.addEventListener('change', (event) => {
        this.handleTriggerChange(event)
      })
    })
  }

  handleTriggerChange(event) {
    const trigger = event.target
    const shouldShow = this.shouldShowForm(trigger)
    
    this.toggleFormVisibility(shouldShow)
    
    // Dispatch custom event for other controllers to react
    this.element.dispatchEvent(new CustomEvent('form:toggle', {
      bubbles: true,
      detail: { 
        trigger,
        visible: shouldShow,
        value: trigger.value
      }
    }))
  }

  shouldShowForm(trigger) {
    if (trigger.type === 'radio' || trigger.type === 'checkbox') {
      return trigger.checked && trigger.value === this.triggerValueValue
    }
    
    if (trigger.tagName === 'SELECT') {
      return trigger.value === this.triggerValueValue
    }
    
    // For text inputs, check if value matches
    return trigger.value === this.triggerValueValue
  }

  toggleFormVisibility(shouldShow) {
    this.toggleTargets.forEach(target => {
      if (shouldShow) {
        target.classList.remove(this.hideClassValue)
      } else {
        target.classList.add(this.hideClassValue)
      }
    })
  }

  initializeFormState() {
    // Check initial state of all triggers
    let shouldShow = false
    
    this.triggerTargets.forEach(trigger => {
      if (trigger.type === 'radio') {
        // For radio buttons, check if any matching one is checked
        if (trigger.checked && trigger.value === this.triggerValueValue) {
          shouldShow = true
        }
      } else if (this.shouldShowForm(trigger)) {
        shouldShow = true
      }
    })
    
    this.toggleFormVisibility(shouldShow)
  }

  // Public method to manually toggle form
  showForm() {
    this.toggleFormVisibility(true)
  }

  hideForm() {
    this.toggleFormVisibility(false)
  }

  // Method to show success message (useful for address autocomplete integration)
  showSuccess(element = null, message = "Fields auto-filled! You can edit them if needed.") {
    if (!this.showSuccessMessageValue) return
    
    const targetElement = element || this.toggleTargets[0]
    if (!targetElement) return
    
    // Add success styling
    targetElement.classList.add(this.successClassValue)
    
    // Create success message
    const successDiv = document.createElement('div')
    successDiv.className = 'text-success small mt-1'
    successDiv.innerHTML = `<i class="bi bi-check-circle-fill me-1"></i>${message}`
    
    // Insert message
    const container = targetElement.parentElement
    if (container) {
      container.appendChild(successDiv)
    }
    
    // Remove after duration
    setTimeout(() => {
      targetElement.classList.remove(this.successClassValue)
      successDiv.style.transition = 'opacity 0.3s'
      successDiv.style.opacity = '0'
      
      setTimeout(() => {
        if (successDiv.parentNode) {
          successDiv.remove()
        }
      }, 300)
    }, this.successDurationValue)
  }

  // Target callbacks for dynamic content
  triggerTargetConnected(trigger) {
    trigger.addEventListener('change', (event) => {
      this.handleTriggerChange(event)
    })
    
    // Check if this new trigger should affect form state
    if (this.shouldShowForm(trigger)) {
      this.showForm()
    }
  }

  // Event listeners for integration with other controllers
  handleAddressUpdated(event) {
    // Can be used to show success message when address is auto-filled
    if (this.showSuccessMessageValue) {
      this.showSuccess(event.target, "Address fields auto-filled! You can edit them if needed.")
    }
  }

  // Method to set trigger value and update form state
  setTriggerValue(value) {
    this.triggerTargets.forEach(trigger => {
      if (trigger.type === 'radio') {
        trigger.checked = (trigger.value === value)
      } else {
        trigger.value = value
      }
      
      // Trigger change event
      trigger.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  // Method to get current form visibility state
  isFormVisible() {
    if (this.toggleTargets.length === 0) return false
    
    return !this.toggleTargets[0].classList.contains(this.hideClassValue)
  }
}